import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { PasswordProtection } from './PasswordProtection';
import { AUTH_CONFIG, isAuthenticated as checkAuth, clearAuthentication } from '../config/auth';
import type { GameRole } from '@/types/game';

interface LandingPageProps {
  onInitialize: (params: {
    role: GameRole;
    sessionId: string;
    userId: string;
    userName: string;
  }) => void;
}

export function LandingPage({ onInitialize }: LandingPageProps) {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuthentication = () => {
      if (checkAuth()) {
        setIsAuthenticated(true);
      } else {
        // Nettoyer les données expirées
        clearAuthentication();
      }
      
      setIsCheckingAuth(false);
    };

    checkAuthentication();
  }, []);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
  };

  // Generate a random session ID
  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // Generate a unique user ID
  const generateUserId = () => {
    return Math.random().toString(36).substring(2, 15);
  };

  // Create a new session
  const createNewSession = () => {
    onInitialize({
      role: 'master',
      sessionId: generateSessionId(),
      userId: generateUserId(),
      userName: t('game.roles.master')
    });
  };

  // Afficher un loader pendant la vérification d'authentification
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <div className="text-center space-y-4">
          <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-600">{t('game.auth.checkingAuth')}</p>
        </div>
      </div>
    );
  }

  // Afficher la protection par mot de passe si non authentifié
  if (!isAuthenticated) {
    return <PasswordProtection onAuthenticated={handleAuthenticated} />;
  }

  // Afficher la landing page normale si authentifié
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('game.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('game.subtitle')}
          </p>
        </div>
        
        {/* Create New Session */}
        <Card className="p-8 border-2 hover:border-blue-200 transition-colors text-center">
          <h3 className="text-2xl font-semibold mb-6 flex items-center justify-center gap-3">
            <div className="h-3 w-3 bg-blue-500 rounded-full" />
            {t('game.session.create')}
          </h3>
          <Button 
            onClick={createNewSession}
            size="lg"
            className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-lg py-3"
          >
            {t('game.session.new')}
          </Button>
        </Card>

        {/* Bouton de déconnexion */}
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              clearAuthentication();
              setIsAuthenticated(false);
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            {t('game.auth.disconnect')}
          </Button>
        </div>
      </div>
    </div>
  );
}
